<template>
  <div style="position: relative;">
    <PhysicalAddressCard
      class="mb-4"
      :item="peParty.physical_address"
    />

    <GenericCollapsableCard>
      <template #title>
        Residence History
      </template>

      <template #default>
        <ResidenceHistoriesCard
          v-for="residence_history of peSortedCharacteristic('residence_histories')"
          :key="residence_history.id"
          :item="residence_history"
          class="mb-4"
          grouped
        />

        <div v-if="peSurveysValidationActive">
          <ResidenceHistoriesCard
            v-for="item of partySurveyCardsByIdentifiers('residence-history-card-')"
            :key="item.identifier"
            :item="item.inputs"
            :survey-identifier="item.identifier"
            class="mb-4"
            survey-validation
            grouped
          />
        </div>
      </template>

      <template #actions>
        <SectionAdderLine
          section="Residence History"
          :has-new-section="peResidenceHistoriesCardHasNewSection"
          @on-add="peResidenceHistoriesCardAddSection"
        />
      </template>
    </GenericCollapsableCard>

    <RelativeSectionAdderLine
      title="Residence History"
      property="residence_histories"
      @on-add="peResidenceHistoriesMultipleCardCreate"
    />

    <div>
      <TravelHistoriesCard
        v-for="travel_history of peSortedCharacteristic('travel_histories')"
        :key="travel_history.id"
        :item="travel_history"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <TravelHistoriesCard
        v-for="item of partySurveyCardsByIdentifiers('travel-histories-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Travel History"
      :has-new-section="peTravelHistoriesCardHasNewSection"
      @on-add="peTravelHistoriesCardAddSection"
    />

    <div>
      <JobHistoriesCard
        v-for="job_history of peSortedCharacteristic('job_histories')"
        :key="job_history.id"
        :item="job_history"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <JobHistoriesCard
        v-for="item of partySurveyCardsByIdentifiers('job-history-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Job History"
      :has-new-section="peJobHistoriesCardHasNewSection"
      @on-add="peJobHistoriesCardAddSection"
    />

    <RelativeSectionAdderLine
      title="Job History"
      property="job_histories"
      @on-add="peJobHistoriesMultipleCardCreate"
    />

    <div>
      <EducationHistoriesCard
        v-for="education_history of peSortedCharacteristic('education_histories')"
        :key="education_history.id"
        :item="education_history"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <EducationHistoriesCard
        v-for="item of partySurveyCardsByIdentifiers('education-history-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Education History"
      :has-new-section="peEducationHistoriesCardHasNewSection"
      @on-add="peEducationHistoriesCardAddSection"
    />

    <div>
      <CriminalHistoriesCard
        v-for="criminal_history of peSortedCharacteristic('criminal_histories')"
        :key="criminal_history.id"
        :item="criminal_history"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <CriminalHistoriesCard
        v-for="item of partySurveyCardsByIdentifiers('criminal-history-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Criminal History"
      :has-new-section="peCriminalHistoriesCardHasNewSection"
      @on-add="peCriminalHistoriesCardAddSection"
    />

    <div>
      <DetentionHistoriesCard
        v-for="detention_history of peSortedCharacteristic('detention_histories')"
        :key="detention_history.id"
        :item="detention_history"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <DetentionHistoriesCard
        v-for="item of partySurveyCardsByIdentifiers('detention-history-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Detention History"
      :has-new-section="peDetentionHistoriesCardHasNewSection"
      @on-add="peDetentionHistoriesCardAddSection"
    />

    <div>
      <ImmigrationStatusHistoriesCard
        v-for="immigration_status of peSortedCharacteristic('immigration_status_histories')"
        :key="immigration_status.id"
        :item="immigration_status"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <ImmigrationStatusHistoriesCard
        v-for="item of partySurveyCardsByIdentifiers('immigration-status-history-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Immigration Status History"
      :has-new-section="peImmigrationStatusHistoriesCardHasNewSection"
      @on-add="peImmigrationStatusHistoriesCardAddSection"
    />

    <v-overlay
      absolute
      :value="!canView"
      :class="{'party-overlay': !canView}"
      class="align-start"
    >
      <v-row
        align="start"
        justify="center"
      >
        <v-col
          cols="12"
          class="text-center"
        >
          <h2>You do not have permissions to view this section.</h2>
        </v-col>
      </v-row>
    </v-overlay>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import Actions from '@/data/Permissions/enums.json'
import { alertHandler } from '@/alertHandler'

import { PARTY_NAMESPACE } from '@/store/party'
import { USER_NAMESPACE } from '@/store/user'

import CriminalHistoriesCard from './SectionCards/HistoryCards/CriminalHistoriesCard'
import DetentionHistoriesCard from './SectionCards/HistoryCards/DetentionHistoriesCard'
import EducationHistoriesCard from './SectionCards/HistoryCards/EducationHistoriesCard'
import GenericCollapsableCard from '@/components/Shared/Cards/GenericCollapsableCard.vue'
import ImmigrationStatusHistoriesCard from './SectionCards/HistoryCards/ImmigrationStatusHistoriesCard'
import JobHistoriesCard from './SectionCards/HistoryCards/JobHistoriesCard'
import PhysicalAddressCard from './SectionCards/PersonalDataCards/PhysicalAddressCard'
import RelativeSectionAdderLine from './SectionTools/RelativeSectionAdderLine.vue'
import ResidenceHistoriesCard from './SectionCards/HistoryCards/ResidenceHistoriesCard'
import SectionAdderLine from './SectionTools/SectionAdderLine'
import TravelHistoriesCard from './SectionCards/HistoryCards/TravelHistoriesCard'

export default {
  name: 'PEHistory',

  components: {
    CriminalHistoriesCard,
    DetentionHistoriesCard,
    EducationHistoriesCard,
    GenericCollapsableCard,
    ImmigrationStatusHistoriesCard,
    JobHistoriesCard,
    PhysicalAddressCard,
    RelativeSectionAdderLine,
    ResidenceHistoriesCard,
    SectionAdderLine,
    TravelHistoriesCard,
  },

  beforeRouteLeave (_, __, next) {
    if (this.peAnyUnlockedSections) {
      const answer = window.confirm('Do you really want to leave? you have unsaved changes!')
      if (answer) {
        this.peLockPartySections()
        next()
      } else next(false)
    } else {
      this.peLockPartySections()
      next()
    }
  },

  props: {
    id: {
      type: String || Number,
      required: true,
    },
  },

  setup () {
    return { ...alertHandler() }
  },

  data () {
    return {
      canView: true,
    }
  },

  computed: {
    userCan () {
      return {
        viewSection: this.can(Actions.Platform.PartyEdit.Sections.History.View),
      }
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peAnyUnlockedSections',
      'peCriminalHistoriesCardHasNewSection',
      'peDetentionHistoriesCardHasNewSection',
      'peEducationHistoriesCardHasNewSection',
      'peImmigrationStatusHistoriesCardHasNewSection',
      'peJobHistoriesCardHasNewSection',
      'peParty',
      'peResidenceHistoriesCardHasNewSection',
      'peSortedCharacteristic',
      'peTravelHistoriesCardHasNewSection',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'partySurveyCardsByIdentifiers',
      'peSurveysValidationActive',
    ]),

    ...mapGetters(USER_NAMESPACE, [
      'can',
    ]),
  },

  created () {
    if (!this.userCan.viewSection) {
      this.canView = false
      this.displayAlert('error', 'You do not have permissions to view this section.')
      return
    }

    this.peGetSelectedPartySection({ id: this.id, section: 'history' }).
      catch(e => {
        if (e.status === 403) this.$router.push({ name: 'resourceAccessDenied', query: { party_id: this.id } })
        else if (e.status === 404) this.$router.push({ name: 'resourceNotFound' })
        else this.displayAlert('error', `An unexpected error occurred: ${e.message}`)
      })
  },

  methods: {
    ...mapActions(PARTY_NAMESPACE, [
      'peCriminalHistoriesCardAddSection',
      'peDetentionHistoriesCardAddSection',
      'peEducationHistoriesCardAddSection',
      'peGetSelectedPartySection',
      'peImmigrationStatusHistoriesCardAddSection',
      'peJobHistoriesCardAddSection',
      'peJobHistoriesMultipleCardCreate',
      'peLockPartySections',
      'peResidenceHistoriesCardAddSection',
      'peResidenceHistoriesMultipleCardCreate',
      'peTravelHistoriesCardAddSection',
    ]),
  },
}
</script>

<style scoped>
.party-overlay {
  margin-top: -75px;
  padding-top: 50vh;
  z-index: 10;
  backdrop-filter: blur(6px);
}
</style>
